import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import { serviceImage } from "../hero/productImages";

const { ArrowRight, ChevronRight, Sparkles } = Icons;
const reveal = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" } };

export const DetailHero = ({ s, d }) => {
  const navigate = useNavigate();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTilt({ x: ((e.clientX - r.left) / r.width - 0.5) * 18, y: -((e.clientY - r.top) / r.height - 0.5) * 14 });
  };
  return (
    <section className="relative bg-[#050C17] overflow-hidden pt-[110px] md:pt-[150px] pb-28 md:pb-36" data-testid="detail-hero">
      <div className="absolute inset-0 pp-hero-grid" />
      <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full blur-[140px] bg-[#0EA5E9]/25" />
      <div className="absolute bottom-0 -right-20 w-[480px] h-[480px] rounded-full blur-[140px] bg-[#10B981]/20" />
      <div className="relative pp-container grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-6" data-testid="detail-breadcrumb">
            <button onClick={() => navigate("/")} className="hover:text-white">Home</button><ChevronRight size={12} />
            <button onClick={() => navigate("/#services")} className="hover:text-white">{s.group}</button><ChevronRight size={12} />
            <span className="text-[#67E8F9]">{s.label}</span>
          </nav>
          <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] text-[#06B6D4] mb-5 px-3 py-1.5 rounded-full border border-[#06B6D4]/30 bg-[#06B6D4]/10">
            <Sparkles size={12} /> {s.kicker}
          </div>
          <h1 className="text-white font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="detail-title">
            {s.title} <span className="pp-grad-text">{s.title_accent}</span> {s.title_tail}
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-xl">{d.tagline}</p>
          <p className="mt-3 text-sm text-slate-400 max-w-xl leading-relaxed">{s.desc}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button onClick={() => navigate("/signup")} data-testid="detail-get-started" className="group inline-flex items-center gap-3 text-[#030712] font-semibold pl-6 pr-2 py-2.5 rounded-full hover:scale-[1.03] transition-transform" style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}>
              Get Started <span className="w-8 h-8 rounded-full bg-[#030712] text-white flex items-center justify-center group-hover:translate-x-1 transition-transform"><ArrowRight size={14} /></span>
            </button>
            <button onClick={() => navigate("/#cta")} data-testid="detail-talk-sales" className="px-6 py-2.5 rounded-full border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors">Talk to Sales</button>
          </div>
        </motion.div>

        <div className="relative h-[340px] md:h-[440px]" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={() => setTilt({ x: 0, y: 0 })}>
          <motion.div initial={{ opacity: 0, rotateY: -30, scale: 0.9 }} animate={{ opacity: 1, rotateY: 0, scale: 1 }} transition={{ duration: 0.9 }}
            className="absolute inset-0 rounded-[36px] border border-[#06B6D4]/25 overflow-hidden transition-transform duration-200 ease-out bg-black"
            style={{ transform: `rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`, transformStyle: "preserve-3d", boxShadow: "0 40px 100px -30px rgba(6,182,212,0.45)" }}>
            <img src={serviceImage(s.visual)} alt={s.label} className="w-full h-full object-cover" data-testid="detail-hero-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050C17]/80 via-transparent to-transparent" />
          </motion.div>
          {(s.highlights || []).slice(0, 2).map((h, i) => (
            <div key={h.title} className={`absolute ${i ? "right-[-6px] bottom-10" : "left-[-10px] top-10"} bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl px-4 py-3 pp-float`} style={{ animationDelay: `${i * 1.2}s` }}>
              <div className="text-white text-sm font-bold">{h.title}</div>
              <div className="text-slate-300 text-[11px] max-w-[180px]">{h.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const StatsStrip = ({ stats }) => (
  <div className="relative z-10 pp-container -mt-16 md:-mt-20">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-0 md:divide-x divide-slate-200 bg-white rounded-3xl shadow-[0_30px_70px_-30px_rgba(13,27,42,0.35)] border border-slate-100 p-4 md:p-7" data-testid="detail-stats">
      {stats.map((st, i) => (
        <motion.div key={st.l} {...reveal} transition={{ delay: i * 0.08 }} className="text-center px-2 py-2">
          <div className="text-2xl md:text-3xl font-extrabold pp-grad-text">{st.v}</div>
          <div className="text-xs md:text-sm text-[#64748B] mt-1">{st.l}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

export const FeatureBento = ({ features }) => (
  <section className="py-20 md:py-28 bg-white" data-testid="detail-features">
    <div className="pp-container">
      <motion.div {...reveal} className="max-w-2xl">
        <span className="text-[#06B6D4] font-semibold uppercase tracking-wider text-sm">Capabilities</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0D1B2A] tracking-tight">Everything you need, <span className="pp-grad-text">nothing you don't</span></h2>
      </motion.div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((f, i) => {
          const Icon = Icons[f.icon] || Sparkles;
          const big = i === 0;
          return (
            <motion.div key={f.title} {...reveal} transition={{ delay: (i % 3) * 0.08 }} data-testid={`detail-feature-${i}`}
              className={`group relative rounded-3xl p-7 border overflow-hidden transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_25px_50px_-25px_rgba(6,182,212,0.45)] ${big ? "bg-[#050C17] border-[#06B6D4]/25 text-white" : "bg-[#F8FAFC] border-slate-100"}`}>
              {big && <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-[#06B6D4]/25 blur-3xl" />}
              <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-transform group-hover:rotate-6 ${big ? "bg-[#06B6D4]/15 text-[#67E8F9]" : "bg-white text-[#0EA5E9] shadow-sm"}`}><Icon size={22} /></div>
              <h3 className={`relative font-bold text-lg ${big ? "text-white" : "text-[#0D1B2A]"}`}>{f.title}</h3>
              <p className={`relative mt-2 text-sm leading-relaxed ${big ? "text-slate-300" : "text-[#475569]"}`}>{f.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);
